import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy
} from 'firebase/firestore';
import { db } from './firebase';

/**
 * Rebus Service
 * Handles all rebus-related Firestore operations
 */

/**
 * Creates a new rebus in Firestore
 * @param {Object} rebusData - Rebus data (title, description, stations, extraTasks, settings)
 * @returns {Promise<Object>} Created rebus with generated ID
 */
export const createRebus = async (rebusData) => {
  try {
    const rebusRef = doc(collection(db, 'rebuses'));
    const rebusId = rebusRef.id;

    // Auto-generate station, question, challenge, and answer IDs
    const processedStations = (rebusData.stations || []).map((station, sIndex) => ({
      id: `s${sIndex + 1}`,
      name: station.name || '',
      description: station.description || '',
      order: station.order || sIndex + 1,
      questions: (station.questions || []).map((question, qIndex) => ({
        id: `s${sIndex + 1}q${qIndex + 1}`,
        text: question.text || '',
        type: question.type || 'text',
        correctAnswer: question.correctAnswer || null,
        answers: question.type === 'multiple_choice'
          ? (question.answers || []).map((answer, aIndex) => ({
              id: `s${sIndex + 1}q${qIndex + 1}a${aIndex + 1}`,
              text: answer.text || '',
              isCorrect: answer.isCorrect || false
            }))
          : [],
        points: question.points || 1
      })),
      challenge: station.challenge
        ? {
            id: `s${sIndex + 1}c`,
            title: station.challenge.title || '',
            description: station.challenge.description || '',
            scoringType: station.challenge.scoringType || 'points',
            maxPoints: station.challenge.maxPoints || 5
          }
        : null
    }));

    // Auto-generate extra task IDs
    const processedExtraTasks = (rebusData.extraTasks || []).map((task, tIndex) => ({
      id: `e${tIndex + 1}`,
      name: task.name || '',
      text: task.text || '',
      type: task.type || 'text',
      correctAnswer: task.correctAnswer || null,
      points: task.points || 100
    }));

    const rebus = {
      id: rebusId,
      title: rebusData.title,
      description: rebusData.description || '',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      stations: processedStations,
      extraTasks: processedExtraTasks,
      settings: {
        deadline: rebusData.settings?.deadline || null,
        latePointsPenalty: rebusData.settings?.latePointsPenalty || 0,
        taskOrder: rebusData.settings?.taskOrder || 'sequential',
        manualScoring: rebusData.settings?.manualScoring || false,
      },
      predefinedTeams: rebusData.predefinedTeams || [],
    };

    await setDoc(rebusRef, rebus);

    return rebus;
  } catch (error) {
    console.error('Error creating rebus:', error);
    throw new Error(`Klarte ikkje opprette rebus: ${error.message}`);
  }
};

/**
 * Fetches a rebus by ID
 * @param {string} rebusId - Rebus document ID
 * @returns {Promise<Object>} Rebus data
 */
export const getRebus = async (rebusId) => {
  try {
    const rebusRef = doc(db, 'rebuses', rebusId);
    const rebusSnap = await getDoc(rebusRef);

    if (!rebusSnap.exists()) {
      throw new Error('Fann ikkje rebusen');
    }

    return rebusSnap.data();
  } catch (error) {
    console.error('Error fetching rebus:', error);
    throw new Error(`Klarte ikkje hente rebus: ${error.message}`);
  }
};

/**
 * Updates an existing rebus
 * @param {string} rebusId - Rebus document ID
 * @param {Object} updates - Fields to update
 * @returns {Promise<void>}
 */
export const updateRebus = async (rebusId, updates) => {
  try {
    const rebusRef = doc(db, 'rebuses', rebusId);

    // Auto-generate IDs if stations are being updated
    if (updates.stations) {
      updates.stations = updates.stations.map((station, sIndex) => ({
        id: station.id || `s${sIndex + 1}`,
        name: station.name || '',
        description: station.description || '',
        order: station.order || sIndex + 1,
        questions: (station.questions || []).map((question, qIndex) => ({
          id: question.id || `s${sIndex + 1}q${qIndex + 1}`,
          text: question.text || '',
          type: question.type || 'text',
          correctAnswer: question.correctAnswer || null,
          answers: question.type === 'multiple_choice'
            ? (question.answers || []).map((answer, aIndex) => ({
                id: answer.id || `s${sIndex + 1}q${qIndex + 1}a${aIndex + 1}`,
                text: answer.text || '',
                isCorrect: answer.isCorrect || false
              }))
            : [],
          points: question.points || 1
        })),
        challenge: station.challenge
          ? {
              id: station.challenge.id || `s${sIndex + 1}c`,
              title: station.challenge.title || '',
              description: station.challenge.description || '',
              scoringType: station.challenge.scoringType || 'points',
              maxPoints: station.challenge.maxPoints || 5
            }
          : null
      }));
    }

    // Auto-generate IDs if extraTasks are being updated
    if (updates.extraTasks) {
      updates.extraTasks = updates.extraTasks.map((task, tIndex) => ({
        id: task.id || `e${tIndex + 1}`,
        name: task.name || '',
        text: task.text || '',
        type: task.type || 'text',
        correctAnswer: task.correctAnswer || null,
        points: task.points || 100
      }));
    }

    const updatedData = {
      ...updates,
      updatedAt: new Date().toISOString()
    };

    await updateDoc(rebusRef, updatedData);

  } catch (error) {
    console.error('Error updating rebus:', error);
    throw new Error(`Klarte ikkje oppdatere rebus: ${error.message}`);
  }
};

/**
 * Deletes a rebus
 * @param {string} rebusId - Rebus document ID
 * @returns {Promise<void>}
 */
export const deleteRebus = async (rebusId) => {
  try {
    const rebusRef = doc(db, 'rebuses', rebusId);
    await deleteDoc(rebusRef);

  } catch (error) {
    console.error('Error deleting rebus:', error);
    throw new Error(`Klarte ikkje slette rebus: ${error.message}`);
  }
};

/**
 * Fetches all rebuses, ordered by most recently updated
 * @returns {Promise<Array>} Array of rebus objects
 */
export const getAllRebuses = async () => {
  try {
    const rebusesRef = collection(db, 'rebuses');
    const q = query(rebusesRef, orderBy('updatedAt', 'desc'));
    const snapshot = await getDocs(q);

    const rebuses = [];
    snapshot.forEach((doc) => {
      rebuses.push({ id: doc.id, ...doc.data() });
    });

    return rebuses;
  } catch (error) {
    console.error('Error fetching all rebuses:', error);
    throw new Error(`Klarte ikkje hente rebusar: ${error.message}`);
  }
};

/**
 * Duplicates an existing rebus with a new ID and " (Kopi)" suffix
 * @param {string} rebusId - ID of the rebus to duplicate
 * @returns {Promise<Object>} The newly created rebus copy
 */
export const duplicateRebus = async (rebusId) => {
  try {
    const original = await getRebus(rebusId);
    const copy = {
      title: original.title + ' (Kopi)',
      description: original.description || '',
      stations: original.stations,
      extraTasks: original.extraTasks,
      settings: original.settings || {},
      predefinedTeams: original.predefinedTeams || [],
    };
    return await createRebus(copy);
  } catch (error) {
    console.error('Error duplicating rebus:', error);
    throw new Error(`Klarte ikkje kopiere Mikkelchase: ${error.message}`);
  }
};
